/* v8 ignore start */
import {Collection} from "@/lib/core/collection/collection";
import {ErrorUtils} from "@/lib/utils/error/errorUtils";

export class CollectionGroup<TKey, T> {
  private readonly _groups: Array<CollectionGroup.IEntry<TKey, T>>;

  public constructor(groups: Array<CollectionGroup.IEntry<TKey, T>>) {
    this._groups = groups;
  }

  public find(filterFn: (entry: CollectionGroup.IEntry<TKey, T>) => boolean): Maybe<T> {
    return this._groups.find(filterFn)?.group;
  }

  public findOrThrow(filterFn: (entry: CollectionGroup.IEntry<TKey, T>) => boolean): T {
    return this.find(filterFn) || ErrorUtils.throwMessage("Unable to find item in group");
  }

  public get keys(): Collection<TKey> {
    return new Collection(this._groups.map((group) => group.key));
  }

  public get values(): Collection<T> {
    return new Collection(this._groups.map((group) => group.group));
  }

  public get entries(): Collection<CollectionGroup.IEntry<TKey, T>> {
    return new Collection(this._groups);
  }

  public get compact(): CollectionGroup<TKey, NonNullable<T>> {
    return new CollectionGroup(this._groups.filter((group) => group.group)) as CollectionGroup<TKey, NonNullable<T>>;
  }

  public map<TResult>(mapFn: (group: CollectionGroup.IEntry<TKey, T>) => TResult): Collection<TResult> {
    return new Collection(this.mapTo(mapFn));
  }

  public mapTo<TResult>(mapFn: (group: CollectionGroup.IEntry<TKey, T>) => TResult): TResult[] {
    return this._groups.map(mapFn);
  }

  public mapEntries<TKey2, T2>(
    mapFn: (group: CollectionGroup.IEntry<TKey, T>) => CollectionGroup.IEntry<TKey2, T2>
  ): CollectionGroup<TKey2, T2> {
    return new CollectionGroup(this._groups.map(mapFn));
  }

  public get isOne(): boolean {
    return this._groups.length === 1;
  }
}

export namespace CollectionGroup {
  export interface IEntry<TKey, T> {
    key: TKey;
    group: T;
  }
}
/* v8 ignore stop */
